import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { useMediaQuery } from '@mantine/hooks'
import Image from 'next/image'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-dataanalysis-5th.module.scss'

interface IProjectOutputProps {
  contents: {
    imgUrl: string
    titlePc: string
    titleM: string
  }[]
}

export const ProjectOutputSliderItem = ({ contents }: IProjectOutputProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings: Settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: false,
    centerMode: true,
    centerPadding: '10.6%',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerMode: false,
          centerPadding: '0',
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrl, titlePc, titleM }) => (
        <div key={titleM} className="pb-7 lg:px-3 lg:pb-[38px]">
          <div className="w-full overflow-hidden rounded">
            <Image
              src={imgUrl}
              layout="fill"
              objectFit="cover"
              className="!relative"
              alt={titleM}
              unoptimized
            />
          </div>
          <div className="mt-2 flex h-[63px] flex-row items-center justify-center whitespace-pre-line px-10 text-center text-sm leading-normal text-label-normal lg:mt-6 lg:h-auto lg:px-0 lg:text-2xl lg:leading-snug">
            {isMobile ? titleM : titlePc}
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface ICurriculumSimpleProps {
  contents: {
    imgUrl: string
    title: string
    desc: string
  }[]
}

export const CurriculumSimpleSliderItem = ({ contents }: ICurriculumSimpleProps) => {
  const settings: Settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: false,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.curriculum_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrl }) => (
          <div className="w-full" key={title}>
            <div className="pl-3">
              <div className="flex h-[166px] flex-col gap-4 rounded bg-trueGray-800 px-4 py-3">
                <div className="h-10 w-10">
                  <Image
                    src={imgUrl as string}
                    layout="fill"
                    objectFit="cover"
                    className="!relative h-full w-full"
                    alt="-"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h6 className="text-sm font-bold text-white">{title}</h6>
                  <p className="text-sm leading-normal text-label-assistive">{desc}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface ISupportProps {
  details: {
    imgUrlPc: string
    imgUrlM: string
    step: string
    title: string
    desc?: string
    contents?: {
      contentsId: string
      contentsInner?: string[]
      contentsCaution?: string
    }[]
  }[]
}

export const SupportSliderItem = ({ details }: ISupportProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: false,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          variableWidth: true,
          dots: true,
          arrows: false,
        },
      },
    ],
  }

  return (
    <Slider {...settings}>
      {details.map(({ imgUrlM, imgUrlPc, step, title, desc, contents }) => (
        <div key={title} className="pb-[40px] lg:pb-[104px]">
          <div className="mx-auto">
            <div className="overflow-hidden pl-4 lg:pl-3 lg:pr-3">
              <div className="flex h-[366px] flex-col overflow-hidden rounded-lg bg-trueGray-800 lg:h-[474px]">
                <div className="w-full overflow-hidden">
                  <Image
                    src={isMobile ? imgUrlM : imgUrlPc}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt={title}
                  />
                </div>
                <div className="flex flex-col p-4 lg:p-6">
                  <p className="mb-1 max-w-fit rounded-sm bg-[rgba(255,119,16,0.1)] px-2 py-0.5 text-xs font-bold text-yellow-450 lg:mb-2 lg:rounded lg:text-base">
                    {step}
                  </p>
                  <h6 className="mb-2 text-lg font-bold leading-normal text-white lg:mb-3 lg:text-2xl">
                    {title}
                  </h6>
                  {desc && (
                    <p className="text-sm leading-normal text-label-assistive lg:text-base lg:text-white">
                      {desc}
                    </p>
                  )}
                  {contents && contents.length > 0 && (
                    <>
                      {contents.map(({ contentsId, contentsInner, contentsCaution }) => (
                        <div key={contentsId}>
                          {contentsInner && (
                            <ul className="flex w-full flex-col">
                              {contentsInner.map(inner => (
                                <li
                                  key={inner}
                                  className="border-b border-[#404040] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-3"
                                >
                                  <p className="text-sm leading-normal text-label-assistive lg:text-base lg:text-white">
                                    {inner}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          )}
                          {contentsCaution && (
                            <p className="mt-2 border-t border-[#404040] pt-1 text-sm text-label-assistive lg:mt-3 lg:pt-3 lg:text-base">
                              {contentsCaution}
                            </p>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IReviewProps {
  reviews: {
    title: string
    who: string
    reviews?: {
      reviewsId: string
      reviewsText: string[]
    }[]
  }[]
}

export const ReviewSliderItem = ({ reviews }: IReviewProps) => {
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: false,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          variableWidth: true,
          dots: true,
          arrows: false,
          infinite: true,
        },
      },
    ],
  }

  return (
    <Slider {...settings}>
      {reviews.map(({ title, reviews, who }) => (
        <div key={title} className="pb-[40px] lg:pb-[104px]">
          <div className="mx-auto">
            <div className="overflow-hidden pl-4 lg:pl-2 lg:pr-2">
              <div className="flex flex-col gap-4 overflow-hidden rounded-lg bg-trueGray-800 p-4">
                <div className="flex flex-col border-b border-[#404040] pb-4 lg:gap-0.5">
                  <img
                    className="h-5 w-5 object-cover"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-dataanalysis-5th/section11_deco.png"
                    alt="-"
                  />
                  <h6 className="text-lg font-bold leading-normal text-white">{title}</h6>
                </div>

                {reviews?.map(({ reviewsId, reviewsText }) => (
                  <ul className="flex flex-col gap-3" key={reviewsId}>
                    {reviewsText.map(inner => (
                      <li key={inner} className="text-base leading-normal text-label-assistive">
                        {inner}
                      </li>
                    ))}
                  </ul>
                ))}

                <p className="w-full text-right text-base text-label-neutral">{who}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    title: string
    desc: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const settings: Settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: false,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.benefit_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrlM }) => (
          <div className="w-full" key={title}>
            <div className="pl-4 lg:pl-8">
              <div>
                <img
                  src={imgUrlM}
                  className="mx-auto mb-[18px] block h-full w-full rounded object-cover"
                  loading="lazy"
                />
                <p className="mb-2 text-base font-bold text-white">{title}</p>
                <p className="whitespace-pre-wrap text-base leading-normal text-label-assistive">
                  {desc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}
